// Chỉ số món đồ, chỉ số người chơi và Lực Chiến (ITEMS §3.7, BALANCE §1.2–1.3). Hàm thuần.
import { BALANCE } from '@data/balance';
import { powerIndex } from '@data/powerScale';
import { affixDef, maybeAffix } from '@data/items/affixes';
import { baseDef } from '@data/items/bases';
import {
  EQUIP_SLOTS,
  type EquipSlot,
  type ItemInstance,
  type ItemLine,
  type SlotKind,
  type StatKey,
  type TriKey,
} from '@data/items/types';
import type { TriOrigin } from '@data/content/techniques';

export type StatBlock = Record<StatKey, number>;
/** Dòng tiện ích / idle có hiệu lực ở P0 (ITEMS §4.3). */
export type UtilityKey =
  'qiRate' | 'offlineEff' | 'idleLoot' | 'itemFind' | 'stoneFind' | 'herbYield' | 'pillToxRes';
export type UtilityBlock = Record<UtilityKey, number>;
const UTILITY_KEYS: readonly UtilityKey[] = [
  'qiRate',
  'offlineEff',
  'idleLoot',
  'itemFind',
  'stoneFind',
  'herbYield',
  'pillToxRes',
];

export function emptyStats(): StatBlock {
  return {
    atkFlat: 0,
    atkPct: 0,
    hpFlat: 0,
    hpPct: 0,
    def: 0,
    critRate: 0,
    critDmg: 0,
    triBody: 0,
    triSpirit: 0,
    triMind: 0,
  };
}

export function emptyUtility(): UtilityBlock {
  return { qiRate: 0, offlineEff: 0, idleLoot: 0, itemFind: 0, stoneFind: 0, herbYield: 0, pillToxRes: 0 };
}

export function slotKindOf(slot: EquipSlot): SlotKind {
  return slot === 'ring1' || slot === 'ring2' ? 'ring' : slot;
}

/** Ô trang bị nhận được phôi loại này. */
export function slotsForKind(kind: SlotKind): EquipSlot[] {
  return kind === 'ring' ? ['ring1', 'ring2'] : [kind];
}

/** P(Giai): Giai = realm × 10 + s. */
export function giaiPower(giai: number): number {
  return powerIndex(Math.floor(giai / 10), giai % 10);
}

/** Giá trị thật của một dòng (đã nhân P hoặc Giai). */
export function lineValue(affixId: string, value: number, giai: number): number {
  const d = maybeAffix(affixId);
  if (!d) return value;
  if (d.unit === 'xP') return value * giaiPower(giai);
  // ×Giai tính theo Giai / 10 (DECISIONS M2): ×Giai nguyên bản cho 8–38 điểm Tam Nguyên mỗi dòng ở LK,
  // gấp nhiều lần phần tu luyện (3 điểm / bước) và làm Lực Chiến vượt xa đường G(n).
  if (d.unit === 'xGiai') return (value * giai) / BALANCE.TRI_LINE_GIAI_DIV;
  return value;
}

/** Hệ số chỉ số gốc: cường hóa +5%/cấp (ITEMS §3.7, §8.1). */
export function baseMult(item: ItemInstance): number {
  return 1 + BALANCE.ENHANCE_STEP * item.enhance;
}

export interface MainStat {
  key: 'atk' | 'hp' | 'def' | 'mana';
  value: number;
}

/** Dòng chính theo ô (K_slot × P(Giai) × hệ số gốc). DEF là rating, không nhân P. */
export function mainStats(item: ItemInstance): MainStat[] {
  const kind = baseDef(item.base).slot;
  const k = BALANCE.ITEM_MAIN_K[kind] as Partial<Record<MainStat['key'], number>>;
  const p = giaiPower(item.giai);
  const m = baseMult(item);
  const out: MainStat[] = [];
  for (const key of ['atk', 'hp', 'def', 'mana'] as const) {
    const v = k[key];
    if (v === undefined) continue;
    out.push({ key, value: key === 'def' ? v * m : v * p * m });
  }
  return out;
}

function addLine(s: StatBlock, u: UtilityBlock, affixId: string, v: number): void {
  const d = maybeAffix(affixId);
  if (!d) return;
  if (d.stat) {
    // Phần trăm lưu ở đơn vị %, ATK/HP/DEF phẳng, Tam Nguyên theo điểm.
    s[d.stat] += d.stat === 'critRate' || d.stat === 'critDmg' ? v / 100 : v;
  } else if ((UTILITY_KEYS as readonly string[]).includes(affixId)) {
    u[affixId as UtilityKey] += v;
  }
}

/** Mọi chỉ số một món cộng cho người mặc (chưa gồm scaling vũ khí). */
export function itemStats(item: ItemInstance): {
  stats: StatBlock;
  utility: UtilityBlock;
  weaponAtk: number;
} {
  const s = emptyStats();
  const u = emptyUtility();
  let weaponAtk = 0;
  const isWeapon = baseDef(item.base).slot === 'weapon';
  for (const m of mainStats(item)) {
    if (m.key === 'atk') {
      if (isWeapon) weaponAtk += m.value;
      else s.atkFlat += m.value;
    } else if (m.key === 'hp') s.hpFlat += m.value;
    else if (m.key === 'def') s.def += m.value;
  }
  for (const imp of baseDef(item.base).implicit)
    addLine(s, u, imp.affix, lineValue(imp.affix, imp.value, item.giai));
  for (const l of item.lines) addLine(s, u, l.affix, lineValue(l.affix, l.value, item.giai));
  return { stats: s, utility: u, weaponAtk };
}

export interface CombatStats {
  atk: number;
  hp: number;
  def: number;
  critRate: number;
  critDmg: number;
}

export interface PlayerBase {
  /** Bước tiểu cảnh n (P người chơi). */
  step: number;
  /** P của người chơi. */
  power: number;
  tri: TriOrigin;
}

export type Loadout = Partial<Record<EquipSlot, ItemInstance | null>>;

export function combatStats(base: PlayerBase, loadout: Loadout): CombatStats & { utility: UtilityBlock } {
  const s = emptyStats();
  const u = emptyUtility();
  s.triBody = base.tri.body;
  s.triSpirit = base.tri.spirit;
  s.triMind = base.tri.mind;
  let weaponAtk = 0;
  let scaling: Record<TriKey, string> | undefined;
  for (const slot of EQUIP_SLOTS) {
    const it = loadout[slot];
    if (!it) continue;
    const r = itemStats(it);
    for (const k of Object.keys(s) as StatKey[]) s[k] += r.stats[k];
    for (const k of UTILITY_KEYS) u[k] += r.utility[k];
    weaponAtk += r.weaponAtk;
    if (slot === 'weapon') scaling = baseDef(it.base).scaling;
  }
  const T = BALANCE.TRI_STAT;
  if (scaling) {
    const coef = BALANCE.WEAPON_SCALE_COEF as Record<string, number>;
    const bonus =
      (coef[scaling.body] ?? 0) * s.triBody +
      (coef[scaling.spirit] ?? 0) * s.triSpirit +
      (coef[scaling.mind] ?? 0) * s.triMind;
    weaponAtk *= 1 + bonus * BALANCE.WEAPON_SCALE_PER_POINT;
  }
  const atkBase = BALANCE.PLAYER_STAT_MULT * BALANCE.ATK_STD * base.power;
  const hpBase = BALANCE.PLAYER_STAT_MULT * BALANCE.HP_STD * base.power;
  return {
    atk:
      (atkBase + s.atkFlat + weaponAtk) *
      (1 + s.atkPct / 100 + T.spiritAtkPct * s.triSpirit + T.mindAtkPct * s.triMind),
    hp: (hpBase + s.hpFlat) * (1 + s.hpPct / 100 + T.bodyHpPct * s.triBody),
    def: BALANCE.PLAYER_DEF_BASE + s.def + T.bodyDef * s.triBody,
    critRate: Math.min(1, BALANCE.CRIT_RATE_BASE + s.critRate),
    critDmg: BALANCE.CRIT_DMG_BASE + s.critDmg,
    utility: u,
  };
}

const CRIT_REF = 1 + BALANCE.CRIT_RATE_BASE * (BALANCE.CRIT_DMG_BASE - 1);

/** Lực Chiến (BALANCE §1.3). Người chơi trần ở P: 100 × P. */
export function combatPower(c: CombatStats): number {
  const atkN = c.atk / (BALANCE.PLAYER_STAT_MULT * BALANCE.ATK_STD);
  const hpN = c.hp / (BALANCE.PLAYER_STAT_MULT * BALANCE.HP_STD);
  return (
    BALANCE.LC_SCALE *
    Math.sqrt(Math.max(0, atkN * hpN)) *
    ((100 + c.def) / (100 + BALANCE.LC_DEF_REF)) *
    ((1 + c.critRate * (c.critDmg - 1)) / CRIT_REF)
  );
}

export function loadoutPower(base: PlayerBase, loadout: Loadout): number {
  return combatPower(combatStats(base, loadout));
}

/** Lực Chiến riêng của một món: phần món cộng thêm cho thân trần (tooltip khối ①). */
export function itemPower(base: PlayerBase, item: ItemInstance): number {
  const slot = slotsForKind(baseDef(item.base).slot)[0]!;
  return loadoutPower(base, { [slot]: item }) - loadoutPower(base, {});
}

/**
 * Thay món vào ô hợp lệ tốt nhất (nhẫn: thay nhẫn làm Lực Chiến tăng nhiều nhất).
 * Trả ô, ΔLC tuyệt đối và tỉ lệ so với LC hiện tại.
 */
export function compareToEquipped(
  base: PlayerBase,
  loadout: Loadout,
  item: ItemInstance,
): { slot: EquipSlot; delta: number; ratio: number; current: number } {
  const current = loadoutPower(base, loadout);
  let best: { slot: EquipSlot; delta: number } | null = null;
  for (const slot of slotsForKind(baseDef(item.base).slot)) {
    if (loadout[slot]?.uid === item.uid) return { slot, delta: 0, ratio: 0, current };
    const d = loadoutPower(base, { ...loadout, [slot]: item }) - current;
    if (!best || d > best.delta) best = { slot, delta: d };
  }
  const b = best!;
  return { ...b, ratio: current > 0 ? b.delta / current : 0, current };
}

/** Vị trí của một dòng trong toàn khoảng T5-min → Cực (0..1), cho "T2 · 74%". */
export function rollPercent(line: ItemLine): number {
  const d = affixDef(line.affix);
  const lo = d.ranges[4]![0];
  const hi = d.ranges[0]![1];
  return hi > lo ? (line.value - lo) / (hi - lo) : 1;
}

export function isPerfect(line: ItemLine): boolean {
  return line.tier === 1 && line.notch >= BALANCE.AFFIX_ROLL_NOTCHES - 1;
}
